import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { StatusVariant } from '~enums';
import DialogPage from '~interfaces/dialogPage';
import { Dimens } from '~themes';

import CustomDialog from './customDialog';

interface Props {
	open: boolean;
	loading?: boolean;
	title: string;
	subtitle?: string;
	pages: DialogPage[];
	variant?: StatusVariant;
	/**
	 * Dialog has requested to close
	 */
	onClose: () => void;
	/**
	 * Dialog has received a confirmation on the last page.
	 */
	onFinish: () => void;
}

const MultiPageDialog = ({
	open,
	loading = false,
	title,
	subtitle,
	pages,
	variant = StatusVariant.Brand,
	onClose,
	onFinish,
}: Props) => {
	const { t } = useTranslation();
	const [pageIndex, setPageIndex] = useState(0);
	const [pageWidth, setPageWidth] = useState(0);
	const [pageHeight, setPageHeight] = useState(0);
	const activePageRef = useRef<HTMLDivElement | null>(null);
	const fromPageRef = useRef<HTMLDivElement | null>(null);
	const toPageRef = useRef<HTMLDivElement | null>(null);
	const [transition, setTransition] = useState<{
		fromIndex: number;
		toIndex: number;
		direction: 1 | -1;
		phase: 'prepare' | 'animate';
	} | null>(null);
	const isAnimating = transition !== null;
	const uiPageIndex = transition?.toIndex ?? pageIndex;

	useLayoutEffect(() => {
		if (transition) return;
		if (!open) return;
		const el = activePageRef.current;
		if (!el) return;

		let rafId = 0;
		const updateSize = () => {
			const rect = el.getBoundingClientRect();
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setPageWidth(rect.width);
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setPageHeight(rect.height);
		};

		updateSize();
		const observer = new ResizeObserver(() => {
			cancelAnimationFrame(rafId);
			rafId = requestAnimationFrame(updateSize);
		});
		observer.observe(el);

		return () => {
			cancelAnimationFrame(rafId);
			observer.disconnect();
		};
	}, [open, pageIndex, pages.length, transition]);

	useLayoutEffect(() => {
		if (!transition || transition.phase !== 'prepare') return;
		const fromEl = fromPageRef.current;
		const toEl = toPageRef.current;
		if (!fromEl || !toEl) return;

		const fromRect = fromEl.getBoundingClientRect();
		const toRect = toEl.getBoundingClientRect();

		// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
		setPageWidth(fromRect.width);
		// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
		setPageHeight(fromRect.height);

		let raf1 = 0;
		let raf2 = 0;
		raf1 = requestAnimationFrame(() => {
			raf2 = requestAnimationFrame(() => {
				setTransition((current) => {
					if (!current || current.phase !== 'prepare') return current;
					return {
						...current,
						phase: 'animate',
					};
				});
				setPageWidth(toRect.width);
				setPageHeight(toRect.height);
			});
		});

		return () => {
			cancelAnimationFrame(raf1);
			cancelAnimationFrame(raf2);
		};
	}, [transition]);

	const goTo = (nextIndex: number) => {
		if (isAnimating) return;

		if (nextIndex < 0) {
			onClose();
			return;
		}

		if (nextIndex >= pages.length) {
			onFinish();
			return;
		}

		if (nextIndex === pageIndex) return;
		setTransition({
			fromIndex: pageIndex,
			toIndex: nextIndex,
			direction: nextIndex > pageIndex ? 1 : -1,
			phase: 'prepare',
		});
	};

	const handleSlideTransitionEnd = (propertyName: string) => {
		if (propertyName !== 'transform') return;
		if (!transition || transition.phase !== 'animate') return;

		setPageIndex(transition.toIndex);
		setTransition(null);
	};

	const resetDialogState = () => {
		setPageIndex(0);
		setPageWidth(0);
		setPageHeight(0);
		setTransition(null);
	};

	useEffect(() => {
		if (pageIndex >= pages.length) {
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setPageIndex(Math.max(0, pages.length - 1));
		}
	}, [pages.length, pageIndex]);

	const buttonHintText = useMemo(
		() =>
			pages.length > 1 ?
				t('stepNofN', {
					ns: 'system',
					current: `${uiPageIndex + 1}`,
					total: `${pages.length}`,
				})
			:	undefined,
		[pages.length, uiPageIndex, t]
	);
	const isFirstPage = uiPageIndex === 0;
	const isLastPage = uiPageIndex === pages.length - 1;
	const abortButtonText =
		isFirstPage ? t('cancel', { ns: 'actions' }) : t('previous', { ns: 'actions' });
	const confirmText = isLastPage ? t('confirm', { ns: 'actions' }) : t('next', { ns: 'actions' });

	return (
		<CustomDialog
			open={open}
			showsCloseButton
			title={title}
			titleAlignment='left'
			subtitle={subtitle}
			loading={loading}
			buttonsHint={buttonHintText}
			abortButtonText={abortButtonText}
			confirmButtonText={confirmText}
			variant={variant}
			slotProps={{
				transition: {
					onExited: resetDialogState,
				},
			}}
			onConfirm={() => {
				if (isAnimating) return;
				if (isLastPage) {
					onFinish();
					return;
				}
				goTo(pageIndex + 1);
			}}
			onDecline={() => {
				if (isAnimating) return;
				if (isFirstPage) {
					onClose();
					return;
				}
				goTo(pageIndex - 1);
			}}
			onClose={onClose}
			disabled={isAnimating || pages[uiPageIndex]?.enableNext === false}
			disableCloseOnBackdropClick
		>
			<Box
				sx={{
					minWidth: { xs: '100%', sm: '350px' },
					width: {
						xs: '100%',
						sm: pageWidth ? Dimens.fixed(pageWidth) : 'fit-content',
					},
					height: pageHeight ? Dimens.fixed(pageHeight) : 'auto',
					transition: 'width 220ms ease, height 220ms ease',
					willChange: 'width, height',
					overflow: 'hidden',
				}}
			>
				{transition ?
					<Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
						<Box
							sx={{
								position: 'absolute',
								top: 0,
								left: 0,
								width: '100%',
								transform:
									transition.phase === 'animate' ?
										`translateX(${-transition.direction * 100}%)`
									:	'translateX(0%)',
								transition: 'transform 220ms ease',
								willChange: 'transform',
							}}
						>
							<Box
								ref={fromPageRef}
								sx={{
									boxSizing: 'border-box',
									width: {
										xs: '100%',
										sm: 'fit-content',
									},
									minWidth: {
										xs: '100%',
										sm: Dimens.fixed(350),
									},
									height: 'fit-content',
								}}
							>
								{pages[transition.fromIndex]?.content}
							</Box>
						</Box>
						<Box
							onTransitionEnd={(event) => {
								handleSlideTransitionEnd(event.propertyName);
							}}
							sx={{
								position: 'absolute',
								top: 0,
								left: 0,
								width: '100%',
								transform:
									transition.phase === 'animate' ?
										'translateX(0%)'
									:	`translateX(${transition.direction * 100}%)`,
								transition: 'transform 220ms ease',
								willChange: 'transform',
							}}
						>
							<Box
								ref={toPageRef}
								sx={{
									boxSizing: 'border-box',
									width: {
										xs: '100%',
										sm: 'fit-content',
									},
									minWidth: {
										xs: '100%',
										sm: Dimens.fixed(350),
									},
									height: 'fit-content',
								}}
							>
								{pages[transition.toIndex]?.content}
							</Box>
						</Box>
					</Box>
				:	<Box
						key={pages[pageIndex]?.key ?? 'empty-page'}
						ref={activePageRef}
						sx={{
							boxSizing: 'border-box',
							width: {
								xs: '100%',
								sm: 'fit-content',
							},
							minWidth: {
								xs: '100%',
								sm: Dimens.fixed(350),
							},
							height: 'fit-content',
						}}
					>
						{pages[pageIndex]?.content}
					</Box>
				}
			</Box>
		</CustomDialog>
	);
};

export default MultiPageDialog;
